import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card/Card';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Badge } from '@/components/ui/Badge/Badge';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { apiFetch } from '@/lib/api';
import { useNotificationStore } from '@/stores/notificationStore';
import type { CreditGrant, CreditTransaction } from '@/services/CreditService';

interface OrgOption { id: string; name: string; slug: string; }

export function AdminCreditGrants() {
  const notify = useNotificationStore();
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [amount, setAmount] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('90');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // View org credits
  const [viewOrg, setViewOrg] = useState('');
  const [viewLoading, setViewLoading] = useState(false);
  const [grants, setGrants] = useState<CreditGrant[]>([]);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [balance, setBalance] = useState<number>(0);

  useEffect(() => {
    apiFetch('/api/admin/orgs')
      .then(r => r.json())
      .then(j => { if (j.success) setOrgs(j.data || []); })
      .catch(() => {});
  }, []);

  const handleGrant = async () => {
    const errs: Record<string, string> = {};
    if (!selectedOrg) errs.org = 'Select an organization';
    if (!amount || parseInt(amount, 10) <= 0) errs.amount = 'Enter a valid credit amount';
    if (!description.trim()) errs.description = 'Description is required';
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const res = await apiFetch('/api/admin/credits/grant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId: selectedOrg,
          amountCredits: parseInt(amount, 10),
          expiresInDays: parseInt(expiresInDays, 10),
          description,
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Grant failed');
      notify.success(`Granted ${Number(amount).toLocaleString()} credits to organization`);
      setAmount('');
      setDescription('');
      setFieldErrors({});
      if (viewOrg === selectedOrg) loadOrgCredits(viewOrg);
    } catch (err: any) {
      notify.error(err.message || 'Grant failed');
    }
    setSubmitting(false);
  };

  const loadOrgCredits = async (orgId: string) => {
    if (!orgId) { setViewOrg(''); setGrants([]); setTransactions([]); setBalance(0); return; }
    setViewOrg(orgId);
    setViewLoading(true);
    try {
      const [summaryRes, grantsRes, txRes] = await Promise.all([
        apiFetch(`/api/admin/credits/${orgId}`).then(r => r.json()),
        apiFetch(`/api/admin/credits/${orgId}/grants`).then(r => r.json()),
        apiFetch(`/api/admin/credits/${orgId}/transactions?limit=50`).then(r => r.json()),
      ]);
      setBalance(Number(summaryRes.data?.balance?.balanceCredits) || 0);
      setGrants(grantsRes.data || []);
      setTransactions(txRes.data?.items || []);
    } catch {}
    setViewLoading(false);
  };

  const formatCredits = (v: unknown) => `${Number(v || 0).toLocaleString()} credits`;

  return (
    <PageContainer>
      <PageHeader title="Credit Grants" description="Grant and manage organization credits" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Grant Form */}
        <Card>
          <CardHeader><CardTitle>Grant Credits</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Organization</label>
              <select
                className={`w-full border rounded px-3 py-2 text-sm bg-background ${fieldErrors.org ? 'border-red-500' : ''}`}
                value={selectedOrg}
                onChange={e => { setSelectedOrg(e.target.value); setFieldErrors(p => ({ ...p, org: '' })); }}
              >
                <option value="">Select organization...</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name} ({o.slug})</option>)}
              </select>
              {fieldErrors.org && <p className="text-sm text-red-500 mt-1">{fieldErrors.org}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Amount (Credits)</label>
              <Input
                type="number" step="1" min="1" value={amount}
                onChange={e => { setAmount(e.target.value); setFieldErrors(p => ({ ...p, amount: '' })); }}
                placeholder="2000"
                error={fieldErrors.amount}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Expires in (days)</label>
              <Input type="number" min="1" max="365" value={expiresInDays} onChange={e => setExpiresInDays(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={description}
                onChange={e => { setDescription(e.target.value); setFieldErrors(p => ({ ...p, description: '' })); }}
                placeholder="Alpha welcome grant" rows={2}
              />
              {fieldErrors.description && <p className="text-sm text-red-500 mt-1">{fieldErrors.description}</p>}
            </div>
            <Button onClick={handleGrant} disabled={submitting} className="w-full">
              {submitting ? 'Granting...' : 'Grant Credits'}
            </Button>
          </CardContent>
        </Card>

        {/* View Credits */}
        <Card>
          <CardHeader><CardTitle>View Organization Credits</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <select
                className="flex-1 border rounded px-3 py-2 text-sm bg-background"
                value={viewOrg}
                onChange={e => loadOrgCredits(e.target.value)}
              >
                <option value="">Select organization...</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name} ({o.slug})</option>)}
              </select>
            </div>
            {viewLoading && <div className="flex justify-center py-4"><Spinner /></div>}
            {!viewLoading && viewOrg && (
              <>
                <div className="text-2xl font-bold">{balance.toLocaleString()} credits</div>
                {grants.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-2">Grants</h4>
                    <div className="space-y-1">
                      {grants.map(g => (
                        <div key={g.id} className="flex items-center justify-between text-sm border-b py-1">
                          <span>{g.description || 'Grant'}</span>
                          <span className="font-medium">{formatCredits(g.remaining_credits)} / {formatCredits(g.amount_credits)}</span>
                          <span className="text-muted-foreground text-xs">exp {new Date(g.expires_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {transactions.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-2">Recent Transactions</h4>
                    <div className="max-h-60 overflow-y-auto space-y-1">
                      {transactions.map(tx => (
                        <div key={tx.id} className="flex items-center justify-between text-xs border-b py-1">
                          <Badge variant={tx.type === 'grant' ? 'success' : tx.type === 'expiry' ? 'warning' : 'secondary'} className="text-xs">{tx.type}</Badge>
                          <span className="font-medium">{tx.type === 'debit' ? '-' : ''}{formatCredits(tx.amount_credits)}</span>
                          <span className="text-muted-foreground truncate max-w-[150px]">{tx.description || '—'}</span>
                          <span className="text-muted-foreground">{new Date(tx.created_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
